import { type FocusEvent, useEffect, useMemo, useRef, useState } from "react";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import { EditDatePicker } from "@/components/ui/EditDatePicker";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EditIconButton } from "@/components/ui/EditIconButton";
import { formatAmount, formatCurrencyInput } from "@/lib/utils/formatters";
import { computePaymentLogBaseFee } from "@/features/application/utils/signaturePaymentHelpers";
import { isApiOne, isLeaseDateBeforeNow } from "@/features/application/pages/applicationDetailHelpers";
import modalStyles from "@/features/application/styles/modals.module.css";
import type { PaymentProcessAdjustmentModalProps, AdjustmentKind } from '../../types/modals'






const toFixedMoney = (value: unknown): string => {
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const toNumber = (value: unknown): number => {
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toYmd = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [month, day, year] = value.split("/");
      return `${year}-${month}-${day}`;
    }
  }
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const toMoneyNumber = (value: unknown): number => {
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export function PaymentProcessAdjustmentModal({
  isOpen,
  onClose,
  application,
  applicantId,
  onSubmit,
}: PaymentProcessAdjustmentModalProps) {
  const tid = "application-modal-payment-process-adjustment";
  const selectedApplicantId = String(applicantId ?? application?.applicant_id ?? "");
  const oldRentAmount = useMemo(() => toFixedMoney(application?.rent_amount), [application]);
  const oldAccessFee = useMemo(() => toFixedMoney(application?.access_fee), [application]);
  const riskRating = application?.risk_rating || "MEDIUM";
  const riskPercentages = useMemo(() => application?.riskPercentages || {
    HIGH: "125.00",
    MEDIUM: "100.00",
    LOW: "50.00",
  }, [application?.riskPercentages]);

  const [rentAmount, setRentAmount] = useState(oldRentAmount);
  const [leaseStartDate, setLeaseStartDate] = useState(toYmd(application?.lease_start_date));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [showReason, setShowReason] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showInvalidRentDialog, setShowInvalidRentDialog] = useState(false);
  const [showPastLeaseDialog, setShowPastLeaseDialog] = useState(false);
  const [rentAmountConfirm, setRentAmountConfirm] = useState<string | null>(null);
  const [isAdjustmentEditMode, setIsAdjustmentEditMode] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const adjustmentInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setRentAmount(oldRentAmount);
    setLeaseStartDate(toYmd(application?.lease_start_date));
    setReason("");
    setNote("");
    setShowReason(false);
    setErrors({});
    setShowInvalidRentDialog(false);
    setShowPastLeaseDialog(false);
    setRentAmountConfirm(null);
    setIsAdjustmentEditMode(false);
    setAdjustmentAmount("");
    setShowPaymentConfirm(false);
  }, [isOpen, oldRentAmount, application?.lease_start_date]);

  const newAccessFee = useMemo(() => {
    const rent = toNumber(rentAmount);
    const percentage = Number(riskPercentages?.[riskRating] ?? 100);
    if (!Number.isFinite(rent) || rent <= 0) return oldAccessFee;
    return ((rent * percentage) / 100).toFixed(2);
  }, [rentAmount, riskPercentages, riskRating, oldAccessFee]);

  const balanceDue = useMemo(
    () => toNumber(newAccessFee) - toNumber(application?.premium_paid),
    [application?.premium_paid, newAccessFee],
  );

  const rentChanged = toNumber(rentAmount) !== toNumber(oldRentAmount);

  const adjustmentKind: AdjustmentKind = useMemo(() => {
    if (!rentChanged) return "";
    if (balanceDue > 0) return "increase";
    if (balanceDue < 0) return "decrease";
    return "";
  }, [balanceDue, rentChanged]);

  const adjustmentType = adjustmentKind === "increase" ? "Invoice" : adjustmentKind === "decrease" ? "Refund" : "";

  const contractPayment = useMemo(() => {
    const payments = Array.isArray(application?.payment_history) ? application.payment_history : [];
    return payments.find((payment) => (
      ["CONTRACT", "CONTRACT_INVOICE"].includes(String(payment?.payment_type || "")) &&
      payment?.transaction_status !== "Refunded"
    ));
  }, [application?.payment_history]);

  const startingBalance = useMemo(() => {
    const payments = Array.isArray(application?.payment_history) ? application.payment_history : [];
    const contractTotal = payments.reduce((sum: number, payment) => {
      if (String(payment?.payment_type || "").includes("CONTRACT") && payment?.payment_status === "Completed") {
        return sum + computePaymentLogBaseFee(payment as Parameters<typeof computePaymentLogBaseFee>[0]);
      }
      return sum;
    }, 0);
    return contractTotal || Number(oldAccessFee) || 0;
  }, [application?.payment_history, oldAccessFee]);

  const defaultAdjustmentAmount = useMemo(() => {
    const accessFee = Number(newAccessFee) || 0;
    if (adjustmentType === "Invoice") {
      return Math.max(0, accessFee - startingBalance);
    }
    if (adjustmentType === "Refund") {
      return Math.max(0, startingBalance - accessFee);
    }
    return 0;
  }, [adjustmentType, newAccessFee, startingBalance]);

  useEffect(() => {
    setAdjustmentAmount(
      adjustmentType ? formatCurrencyInput(defaultAdjustmentAmount, true) : "",
    );
    setIsAdjustmentEditMode(false);
  }, [adjustmentType, defaultAdjustmentAmount]);

  const paymentCalculator = useMemo(() => {
    const parsedAdjustment = Number(String(adjustmentAmount).replace(/[^0-9.-]/g, ""));
    const adjustment =
      adjustmentAmount && Number.isFinite(parsedAdjustment)
        ? parsedAdjustment
        : defaultAdjustmentAmount;
    if (adjustmentType === "Invoice") {
      return {
        totalBalance: startingBalance,
        adjustment,
        balanceOwed: adjustment,
        refundAmount: 0,
      };
    }
    if (adjustmentType === "Refund") {
      return {
        totalBalance: startingBalance - adjustment,
        adjustment,
        balanceOwed: 0,
        refundAmount: adjustment,
      };
    }
    return {
      totalBalance: 0,
      adjustment: 0,
      balanceOwed: 0,
      refundAmount: 0,
    };
  }, [adjustmentAmount, adjustmentType, defaultAdjustmentAmount, startingBalance]);

  const paymentAdjustment = useMemo(() => {
    if (!adjustmentType || !contractPayment?.payment_log_id) return undefined;
    return {
      payment_log_id: contractPayment.payment_log_id,
      requestData: {
        adjustment_type:
          adjustmentType === "Invoice"
            ? "contract_invoice"
            : "refund_contract_partial",
        adjustment_reason:
          adjustmentType === "Invoice"
            ? "RENT_INCREASED"
            : "RENT_DECREASED",
        reason:
          adjustmentType === "Invoice"
            ? "Rent Increased"
            : "Rent Decreased",
        refund_amount: paymentCalculator.adjustment,
        notes: note.trim(),
        ...(adjustmentType === "Invoice" ? { apply_surcharge: "0" } : {}),
        adjustment_data: {
          totalBalance:
            adjustmentType === "Invoice"
              ? startingBalance
              : startingBalance - paymentCalculator.adjustment,
          startBalance: startingBalance,
          adjustmentPayment: paymentCalculator.adjustment,
          balanaceOwnPayment:
            adjustmentType === "Invoice" ? paymentCalculator.adjustment : 0,
          refundAmount:
            adjustmentType === "Refund" ? paymentCalculator.adjustment : 0,
          refundFee: 0,
          certificate_fee: toMoneyNumber(newAccessFee),
        },
      },
    };
  }, [
    adjustmentType,
    contractPayment,
    newAccessFee,
    note,
    paymentCalculator.adjustment,
    startingBalance,
  ]);

  const payerName = String(
    contractPayment?.payer_name ??
      contractPayment?.paid_by ??
      contractPayment?.card_holder_name ??
      "",
  ).trim();
  const cardDisplay = useMemo(() => {
    const cardNumber = String(
      contractPayment?.card_number ?? contractPayment?.card_no ?? "",
    ).trim();
    if (!cardNumber) return "";
    if (cardNumber.includes("X") || cardNumber.length > 4) return cardNumber;
    return `XXXXXXXXXXXX${cardNumber}`;
  }, [contractPayment]);
  const paymentConfirmMessage = useMemo(() => {
    if (adjustmentType !== "Refund") {
      return "Do you want to Process Payment Adjustment?";
    }
    return [
      "Do you want to Refund Payment?",
      `Action: Refund and send a notice to: ${payerName}`,
      "",
      `For: ${contractPayment?.payment_type ?? "CONTRACT"}`,
      `Payment Method: ${contractPayment?.payment_source ?? ""}`,
      `Refund Amount: ${formatAmount(paymentCalculator.refundAmount)}`,
      ...(cardDisplay ? [`Card: ${cardDisplay}`] : []),
    ].join("\n");
  }, [
    adjustmentType,
    cardDisplay,
    contractPayment?.payment_source,
    contractPayment?.payment_type,
    payerName,
    paymentCalculator.refundAmount,
  ]);

  const handleRentChange = (value: string) => {
    setRentAmount(value.replace(/[^0-9.]/g, ""));
    setShowReason(true);
    setErrors((prev) => ({ ...prev, rent_amount: "" }));
  };

  const isInvalidRentAmount = (value = rentAmount) => {
    const rent = toNumber(value);
    return !value || !Number.isFinite(rent) || rent <= 0;
  };

  const showInvalidRentAmountPopup = () => {
    setErrors((prev) => ({
      ...prev,
      rent_amount: "'Monthly Rent Amount' is required.",
    }));
    setShowInvalidRentDialog(true);
  };

  const shouldConfirmRentAmount = (value: unknown) => {
    const rent = Number(String(value).replace(/[^0-9.]/g, ""));
    return Number.isFinite(rent) && (rent < 500 || rent > 2500);
  };

  const handleRentBlur = (event: FocusEvent<HTMLInputElement>) => {
    const rentAmountValue = event.currentTarget.value;
    const cleanedRent = rentAmountValue.replace(/[^0-9.]/g, "");

    if (isInvalidRentAmount(cleanedRent)) {
      showInvalidRentAmountPopup();
      return;
    }

    if (shouldConfirmRentAmount(cleanedRent)) {
      setRentAmountConfirm(formatAmount(cleanedRent));
      return;
    }

    setRentAmount(toFixedMoney(cleanedRent));
  };

  const confirmRentAmount = () => {
    setRentAmount(toFixedMoney(rentAmount));
    setRentAmountConfirm(null);
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (isInvalidRentAmount()) {
      nextErrors.rent_amount = "'Monthly Rent Amount' is required.";
    }
    if (!leaseStartDate) {
      nextErrors.lease_start_date = "'Lease Start Date' is required.";
    }
    if (showReason && !reason) {
      nextErrors.rent_lease_reason = "'Reason' is required.";
    }
    if (isAdjustmentEditMode && adjustmentAmount.trim() === "") {
      nextErrors.adjustment_amount = "'Adjustment' is required.";
    }
    if (isAdjustmentEditMode && !note.trim()) {
      nextErrors.note = "Mandatory if Manual Adjustment is made";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const shouldConfirmPastLeaseDate = () =>
    isLeaseDateBeforeNow(leaseStartDate) &&
    !isApiOne(application?.dont_update_lease_date);

  const submitConfirmed = async () => {
    setSubmitting(true);
    setShowPaymentConfirm(false);
    try {
      const coResident = application?.co_resident_application?.[selectedApplicantId];
      await onSubmit({
        fk_property_id: application?.property?.property_id,
        fk_agent_id: application?.agent?.user_id,
        rent_amount: toFixedMoney(rentAmount),
        lease_start_date: leaseStartDate,
        lease_duration: application?.lease_duration,
        invite_code: application?.invite_code,
        other_individuals: Number(coResident?.other_individuals || 0),
        ...(riskRating ? { risk_rating: riskRating } : {}),
        ...(showReason ? { rent_lease_reason: reason } : {}),
        ...(note.trim() ? { notes: note.trim() } : {}),
        ...(paymentAdjustment ? { paymentAdjustment } : {}),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (isInvalidRentAmount()) {
      showInvalidRentAmountPopup();
      return;
    }
    if (!validate()) return;
    if (paymentAdjustment) {
      setShowPaymentConfirm(true);
      return;
    }
    if (shouldConfirmPastLeaseDate()) {
      setShowPastLeaseDialog(true);
      return;
    }
    void submitConfirmed();
  };

  const handlePaymentConfirm = () => {
    setShowPaymentConfirm(false);
    if (shouldConfirmPastLeaseDate()) {
      setShowPastLeaseDialog(true);
      return;
    }
    void submitConfirmed();
  };

  const focusLeaseDateField = () => {
    window.setTimeout(() => {
      const leaseInput = document.querySelector(
        `[data-testid="${tid}-lease-date-input"] .edit-date-picker-input`,
      ) as HTMLInputElement | null;
      leaseInput?.focus();
      leaseInput?.scrollIntoView({ block: "center" });
    }, 0);
  };

  return (
    <>
      <OnScreenModal
        isOpen={isOpen}
        title="Payment Process Adjustment"
        onClose={onClose}
        maxWidth="max-w-2xl"
        headerVariant="centeredTitle"
        bodyScrollOnly
        footer={
          <div className={modalStyles.modalFooterActionGroup}>
            <button
              type="button"
              onClick={onClose}
              className={modalStyles.modalActionSecondary}
              data-testid={`${tid}-cancel-btn`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className={modalStyles.modalActionPrimary}
              data-testid={`${tid}-submit-button`}
            >
              {submitting ? "Saving..." : "Submit"}
            </button>
          </div>
        }
        overlayDataTestId={tid}
        panelDataTestId={`${tid}-panel`}
        titleDataTestId={`${tid}-title`}
        closeButtonDataTestId={`${tid}-close-button`}
      >
        <div className="px-2 pb-2" data-testid={`${tid}-body`}>
        <div className="mb-4 text-center text-[15px] font-bold text-[#232946]">
          Application Risk Rating : {riskRating}
        </div>

        <div className="max-w-full overflow-x-auto">
        <table className="min-w-[560px] w-full border-collapse border border-slate-200 text-sm">
          <colgroup>
            <col className="w-[40%]" />
            <col className="w-[30%]" />
            <col className="w-[30%]" />
          </colgroup>
          <thead>
            <tr>
              <th className="border border-slate-200 px-3 py-2 font-semibold" />
              <th className="border border-slate-200 px-3 py-2 text-left font-semibold">Old ($)</th>
              <th className="border border-slate-200 px-3 py-2 text-left font-semibold">New ($)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-200 px-3 py-2 align-middle">Rent Amount</td>
              <td className="border border-slate-200 px-3 py-2 align-middle">{oldRentAmount}</td>
              <td className="border border-slate-200 px-3 py-2">
                <input
                  type="text"
                  value={rentAmount}
                  onBlur={handleRentBlur}
                  onChange={(e) => handleRentChange(e.target.value)}
                  className="h-9 w-full rounded border border-slate-200 px-3 text-sm outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
                  placeholder="Monthly Rent"
                  data-testid={`${tid}-rent-amount-input`}
                />
                {errors.rent_amount ? <div className="pt-1 text-xs text-red-600">{errors.rent_amount}</div> : null}
              </td>
            </tr>
            <tr>
              <td className="border border-slate-200 px-3 py-2">Access Fee</td>
              <td className="border border-slate-200 px-3 py-2">{oldAccessFee}</td>
              <td className="border border-slate-200 px-3 py-2">{newAccessFee}</td>
            </tr>
            <tr>
              <td className="border border-slate-200 px-3 py-2 align-middle">Lease Date</td>
              <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                <div data-testid={`${tid}-lease-date-input`}>
                  <EditDatePicker
                    value={leaseStartDate}
                    onChange={(value) => {
                      setLeaseStartDate(value);
                      setShowReason(true);
                      setErrors((prev) => ({ ...prev, lease_start_date: "" }));
                    }}
                    allowPastDates
                    allowFutureDates
                    displayFormat="mdy"
                    placeholder="MM/DD/YYYY"
                    calendarPortalId="liberty-datepicker-payment-process-lease-date"
                  />
                </div>
                {errors.lease_start_date ? <div className="pt-1 text-xs text-red-600">{errors.lease_start_date}</div> : null}
              </td>
            </tr>
            {showReason ? (
              <tr>
                <td className="border border-slate-200 px-3 py-2 align-middle">Reason</td>
                <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                  <select
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      setErrors((prev) => ({ ...prev, rent_lease_reason: "" }));
                    }}
                    className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
                    data-testid={`${tid}-reason-select`}
                  >
                    <option value="">Select Reason</option>
                    <option value="Rent Changed - Prior to Occupancy">Rent Changed - Prior to Occupancy</option>
                    <option value="Rent Changed - Transfer within Complex">Rent Changed - Transfer within Complex</option>
                    <option value="Lease Start Moved">Lease Start Moved</option>
                  </select>
                  {errors.rent_lease_reason ? <div className="pt-1 text-xs text-red-600">{errors.rent_lease_reason}</div> : null}
                </td>
              </tr>
            ) : null}
            {adjustmentType ? (
              <tr>
                <td className="border border-slate-200 px-3 py-2">Adjustment Type</td>
                <td className="border border-slate-200 px-3 py-2" colSpan={2}>{adjustmentType}</td>
              </tr>
            ) : null}
            {adjustmentType ? (
              <>
                <tr>
                  <th className="border border-slate-200 px-3 py-2 text-left" colSpan={3}>
                    Payment Calculator
                  </th>
                </tr>
                <tr>
                  <td className="border border-slate-200 px-3 py-2">Access Fee</td>
                  <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                    {formatAmount(newAccessFee)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-slate-200 px-3 py-2">Starting Balance</td>
                  <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                    {formatAmount(startingBalance)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-slate-200 px-3 py-2 align-middle">Adjustment</td>
                  <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                    <div className="relative">
                      <input
                        ref={adjustmentInputRef}
                        type="text"
                        value={adjustmentAmount}
                        readOnly={!isAdjustmentEditMode}
                        onChange={(event) => {
                          setAdjustmentAmount(formatCurrencyInput(event.target.value));
                          setErrors((prev) => ({ ...prev, adjustment_amount: "", note: "" }));
                        }}
                        onBlur={() =>
                          setAdjustmentAmount((value) => formatCurrencyInput(value, true))
                        }
                        className={`h-9 w-full rounded border border-slate-200 px-3 text-sm text-slate-700 outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff] ${
                          isAdjustmentEditMode ? "bg-white" : "bg-slate-50"
                        } ${isAdjustmentEditMode ? "" : "pr-10"}`}
                        data-testid={`${tid}-calculator-adjustment-input`}
                      />
                      {!isAdjustmentEditMode ? (
                        <EditIconButton
                          aria-label="Edit adjustment amount"
                          title="Edit"
                          onClick={() => {
                            setIsAdjustmentEditMode(true);
                            window.setTimeout(() => adjustmentInputRef.current?.focus(), 0);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          data-testid={`${tid}-calculator-adjustment-edit-button`}
                        />
                      ) : null}
                    </div>
                    {errors.adjustment_amount ? (
                      <div className="pt-1 text-xs text-red-600">{errors.adjustment_amount}</div>
                    ) : null}
                  </td>
                </tr>
                {adjustmentType === "Refund" ? (
                  <tr>
                    <td className="border border-slate-200 px-3 py-2">Refund Amount</td>
                    <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                      {formatAmount(paymentCalculator.refundAmount)}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="border border-slate-200 px-3 py-2">Balance Owed</td>
                    <td className="border border-slate-200 px-3 py-2" colSpan={2}>
                      {formatAmount(paymentCalculator.balanceOwed)}
                    </td>
                  </tr>
                )}
              </>
            ) : null}
          </tbody>
        </table>
        </div>

        {adjustmentType ? (
          <div className="mt-4">
            <label className="mb-2 block text-sm font-semibold text-[#232946]" htmlFor={`${tid}-note-input`}>
              Note
              {isAdjustmentEditMode ? <span className="text-red-600"> *</span> : null}
            </label>
            <textarea
              id={`${tid}-note-input`}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setErrors((prev) => ({ ...prev, note: "" }));
              }}
              className="h-20 w-full resize-none rounded border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
              data-testid={`${tid}-note-input`}
            />
            {errors.note ? <div className="pt-1 text-xs text-red-600">{errors.note}</div> : null}
          </div>
        ) : null}

        </div>
      </OnScreenModal>

      <ConfirmDialog
        isOpen={showInvalidRentDialog}
        title="Invalid Rent Amount"
        message="Rent amount must be greater than 0."
        primaryLabel="Ok"
        onPrimary={() => setShowInvalidRentDialog(false)}
        secondaryLabel=""
        onSecondary={() => setShowInvalidRentDialog(false)}
        onDismiss={() => setShowInvalidRentDialog(false)}
        primaryFirst
        overlayDataTestId={`${tid}-invalid-rent-dialog`}
        primaryButtonDataTestId={`${tid}-invalid-rent-ok-button`}
        closeButtonDataTestId={`${tid}-invalid-rent-close-button`}
      />
      <ConfirmDialog
        isOpen={rentAmountConfirm !== null}
        title="Rent Amount"
        message={`Please confirm the rent amount as ${rentAmountConfirm ?? ""}`}
        primaryLabel="Ok"
        onPrimary={confirmRentAmount}
        secondaryLabel=""
        onSecondary={() => setRentAmountConfirm(null)}
        onDismiss={() => setRentAmountConfirm(null)}
        hideClose
        primaryFirst
        overlayDataTestId={`${tid}-rent-amount-confirm`}
        primaryButtonDataTestId={`${tid}-rent-amount-confirm-ok-button`}
      />
      <ConfirmDialog
        isOpen={showPastLeaseDialog}
        title="Lease Start Date"
        message="Does the Lease Start Date need to be Adjusted into the future?"
        primaryLabel="Yes"
        secondaryLabel="No"
        primaryFirst
        presentation="saveChanges"
        actionStyle="application"
        onPrimary={() => {
          setShowPastLeaseDialog(false);
          focusLeaseDateField();
        }}
        onSecondary={() => {
          setShowPastLeaseDialog(false);
          void submitConfirmed();
        }}
        onDismiss={() => setShowPastLeaseDialog(false)}
        overlayDataTestId={`${tid}-past-lease-dialog`}
      />
      <ConfirmDialog
        isOpen={showPaymentConfirm}
        title="Confirm"
        message={paymentConfirmMessage}
        primaryLabel="Yes"
        secondaryLabel="No"
        onPrimary={handlePaymentConfirm}
        onSecondary={() => setShowPaymentConfirm(false)}
        onDismiss={() => setShowPaymentConfirm(false)}
        primaryFirst
        overlayDataTestId={`${tid}-payment-adjustment-confirm`}
        primaryButtonDataTestId={`${tid}-payment-adjustment-confirm-yes-button`}
        secondaryButtonDataTestId={`${tid}-payment-adjustment-confirm-no-button`}
      />
    </>
  );
}
